import { HttpStatusCode } from "@solidjs/start";

import { A, useParams } from "@solidjs/router";
import { createMemo, createResource, createSignal, For, onMount, Show, Suspense } from "solid-js";
import { Link, Check, Share2, FileText, ChevronLeft, ChevronRight } from "lucide-solid";
import ProjectInfobox from "@/pages/portfolio/ProjectInfobox";
import { useLocale } from "@/context/locale";
import LinkButton from "@/components/link-button";
import { RowButtons } from "@/components/button-group";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { addHeadingAnchors, parseReadme, stripLeadingH1, README_PROSE } from "@/lib/readme";
import DocsShell from "@/components/docs/DocsShell";
import { ALL_DOCS, findDoc, localDesc } from "./resources";

const README_JSON = import.meta.glob("/src/data/readmes/*.json", { import: "default" });

function loadReadme([readmeKey, locale]) {
  const loader = README_JSON[`/src/data/readmes/${readmeKey}.json`];
  if (!loader) return Promise.resolve("");
  return loader().then((doc) =>
    parseReadme(stripLeadingH1(doc[`readme_${locale}`] || doc.readme), doc.media || {})
  );
}

export default function DocPage() {
  const { t, prefix, locale } = useLocale();
  const params = useParams();
  const slug = () => params.slug ?? params.resource;
  const pageSlug = () => params.page ?? "";
  const project = createMemo(() => findDoc(slug()));
  const activePage = createMemo(() => project()?.pages?.find((pg) => pg.slug === pageSlug()));
  const found = () => project() && (!pageSlug() || activePage());
  const heading = () => activePage()?.title ?? project()?.name;
  const desc = () => (project() ? localDesc(project(), locale) : "");
  const readmeKey = () => (pageSlug() ? `${slug()}--${pageSlug()}` : slug());
  const [docHtml] = createResource(() => [readmeKey(), locale], loadReadme, { initialValue: "" });
  const doc = () => addHeadingAnchors(docHtml());
  const portfolioHref = `${prefix}/portfolio`;
  const projectHref = () => `${portfolioHref}/${slug()}`;

  // Every project overview and its chapters, in reading order, so prev/next can
  // cross from one project's last chapter into the next project's overview.
  const sequence = createMemo(() => {
    const seq = [];
    for (const p of ALL_DOCS) {
      seq.push({ slug: p.slug, page: "", label: p.name, href: `${portfolioHref}/${p.slug}` });
      for (const pg of p.pages ?? []) {
        seq.push({ slug: p.slug, page: pg.slug, label: pg.title, href: `${portfolioHref}/${p.slug}/${pg.slug}` });
      }
    }
    return seq;
  });
  const sequenceIndex = () => sequence().findIndex((e) => e.slug === slug() && e.page === pageSlug());
  const prevEntry = () => (sequenceIndex() > 0 ? sequence()[sequenceIndex() - 1] : null);
  const nextEntry = () => {
    const i = sequenceIndex();
    return i >= 0 && i < sequence().length - 1 ? sequence()[i + 1] : null;
  };

  const [copied, setCopied] = createSignal(false);
  const [canNativeShare, setCanNativeShare] = createSignal(false);
  onMount(() => {
    const mobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    setCanNativeShare(mobile && !!navigator.share);
  });

  function copyLink() {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function openShare(platform) {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(heading() ?? "");
    const targets = {
      x: `https://x.com/intent/tweet?url=${url}&text=${title}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    };
    window.open(targets[platform], "_blank", "noopener,noreferrer");
  }

  function nativeShare() {
    navigator.share({ title: heading(), text: project()?.desc || "", url: window.location.href });
  }

  return (
    <DocsShell activeArea="portfolio" activeSlug={slug()} activeSubpage={pageSlug()} title={found() ? heading() : slug()}>
      <Show
        when={found()}
        fallback={
          <div class="flex flex-col gap-4">
            <HttpStatusCode code={404} />
            <p class="text-muted-foreground">{t("resources.notFound")}</p>
            <A href={portfolioHref} class="text-foreground underline">{t("breadcrumb.portfolio")}</A>
          </div>
        }
      >
        <div class="max-w-5xl flex flex-col gap-8">
          <div class="relative flex flex-col gap-5 pb-8 border-b border-border">
            <Show when={activePage()}>
              <A href={projectHref()} class="lux-rise inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <ChevronLeft size={14} />
                {project().name}
              </A>
            </Show>
            <Show when={project().lang && !activePage()}>
              <Badge class="lux-rise w-fit bg-purple text-background hover:bg-purple">{project().lang}</Badge>
            </Show>
            <h1 class="lux-rise font-black text-4xl md:text-6xl tracking-tight leading-[0.95] text-foreground" style={{ "animation-delay": "60ms" }}>{heading()}</h1>
            <span class="lux-rise h-px w-16 bg-purple" aria-hidden="true" style={{ "animation-delay": "120ms" }} />
            <Show when={desc() && !activePage()}>
              <p class="lux-rise text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl" style={{ "animation-delay": "160ms" }}>{desc()}</p>
            </Show>
            <div class="lux-rise" style={{ "animation-delay": "220ms" }}>
            <RowButtons>
              <Show when={project().url}>
                <LinkButton href={project().url} external icon={
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.303 3.438 9.8 8.205 11.387.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0 1 12 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222 0 1.606-.015 2.896-.015 3.286 0 .322.216.694.825.576C20.565 21.795 24 17.298 24 12c0-6.63-5.37-12-12-12z"/></svg>
                }>
                  GitHub
                </LinkButton>
              </Show>
              <Show when={project().homepage}>
                <LinkButton href={project().homepage} external icon={<FileText size={15} />}>
                  {t("portfolio.documentation")}
                </LinkButton>
              </Show>

              <span class="text-border select-none">|</span>

              <Show
                when={canNativeShare()}
                fallback={
                  <>
                    <Button onClick={copyLink} variant="outline" icon={copied() ? <Check size={15} /> : <Link size={15} />}>
                      {copied() ? t("portfolio.linkCopied") : t("portfolio.copyLink")}
                    </Button>
                    <Button onClick={() => openShare("x")} variant="outline" icon={
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622Zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    }>
                      {t("portfolio.shareOnX")}
                    </Button>
                    <Button onClick={() => openShare("linkedin")} variant="outline" icon={
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                    }>
                      {t("portfolio.shareOnLinkedIn")}
                    </Button>
                  </>
                }
              >
                <Button onClick={nativeShare} variant="outline" icon={<Share2 size={15} />}>
                  {t("portfolio.share")}
                </Button>
              </Show>
            </RowButtons>
            </div>
          </div>

          <div class="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-14 items-start">
            <ProjectInfobox
              project={project()}
              activePage={pageSlug()}
              class="lg:col-start-2 lg:row-start-1 lg:sticky lg:top-[calc(var(--nav-height,0px)+1.5rem)]"
            />

            <div class="lg:col-start-1 lg:row-start-1 flex flex-col gap-8 min-w-0">
              <Suspense fallback={<div class="min-h-40" />}>
                <Show when={doc().headings.length > 1}>
                  <nav class="glass-panel rounded-2xl w-fit min-w-56 px-5 py-4 flex flex-col gap-2">
                    <span class="text-sm font-bold text-foreground">{t("portfolio.info.contents")}</span>
                    <ol class="flex flex-col gap-1 list-decimal pl-5 text-sm">
                      <For each={doc().headings}>
                        {(h) => (
                          <li>
                            <a href={`#${h.id}`} class="text-foreground underline-offset-4 hover:underline">{h.text}</a>
                          </li>
                        )}
                      </For>
                    </ol>
                  </nav>
                </Show>
                <div
                  class={`${README_PROSE} [&_h1]:scroll-mt-24 [&_h2]:scroll-mt-24`}
                  // eslint-disable-next-line solid/no-innerhtml
                  innerHTML={doc().html}
                />
              </Suspense>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-3 pt-6 border-t border-border">
            <Show when={prevEntry()} fallback={<span />}>
              <A
                href={prevEntry().href}
                class="flex flex-col gap-1 rounded-lg border border-border p-4 min-w-0 hover:border-foreground/30 transition-colors"
              >
                <span class="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <ChevronLeft size={14} /> {t("portfolio.previous")}
                </span>
                <span class="text-sm font-medium text-foreground truncate">{prevEntry().label}</span>
              </A>
            </Show>
            <Show when={nextEntry()} fallback={<span />}>
              <A
                href={nextEntry().href}
                class="flex flex-col gap-1 rounded-lg border border-border p-4 min-w-0 text-right hover:border-foreground/30 transition-colors"
              >
                <span class="flex items-center justify-end gap-1.5 text-xs text-muted-foreground">
                  {t("portfolio.next")} <ChevronRight size={14} />
                </span>
                <span class="text-sm font-medium text-foreground truncate">{nextEntry().label}</span>
              </A>
            </Show>
          </div>
        </div>
      </Show>
    </DocsShell>
  );
}
